/**
 * Purpose: Sidebar/summary card with labeled rows (contact info, hours, society…).
 * Inputs: InfoCard — title, action (node), children (typically InfoRow list).
 *         InfoRow — icon (node), label, href (renders value as link), children (value).
 * Outputs: bordered card; rows are a definition-list for accessibility.
 */
import React from 'react';

export const InfoRow = ({ icon = null, label, href = null, children, ...anchorProps }) => {
  const value = href ? (
    <a
      href={href}
      className="font-medium text-brand-700 hover:text-brand-800 hover:underline underline-offset-2 break-words dark:text-brand-400 dark:hover:text-brand-300"
      {...anchorProps}
    >
      {children}
    </a>
  ) : (
    <span className="font-medium text-gray-900 break-words dark:text-gray-100">{children}</span>
  );

  return (
    <div className="flex items-start gap-3 py-2.5">
      {icon && (
        <span className="mt-0.5 flex-shrink-0 text-brand-600 dark:text-brand-400 [&>svg]:h-4 [&>svg]:w-4" aria-hidden>
          {icon}
        </span>
      )}
      <div className="min-w-0 text-sm">
        {label && <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>}
        <dd className="m-0">{value}</dd>
      </div>
    </div>
  );
};

const InfoCard = ({ title = null, action = null, className = '', children }) => (
  <section
    className={[
      'rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5',
      'dark:border-gray-700 dark:bg-gray-900',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {(title || action) && (
      <div className="mb-2 flex items-center justify-between gap-2">
        {title && (
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
        )}
        {action}
      </div>
    )}
    <dl className="divide-y divide-gray-100 dark:divide-gray-800">{children}</dl>
  </section>
);

export default InfoCard;
